import { ErrorBoundary } from '@/components/Rollbar';
import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { JournalInfoCards } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Cards/JournalInfoCards';
import { ShowRemainingButton } from '@/components/InvoiceReportDetailDrawer/parts/ShowRemainingButton';
import { ControlArea } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/ControlArea';
import {
  JournalLayout,
  JournalLayouts,
  useJournalLayout,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalLayout';
import { useJournalRemaining } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalRemaining';
import {
  useDefaultPagination,
  useJournalEntries,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalEntries';
import { filterColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Columns/helpers';
import { useColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Columns';
import {
  ErrorJournalInfoTable,
  LoadingJournalInfoTable,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Table';
import {
  JournalInfoTable,
  type InternalTableProps,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/JournalInfoTable';
import { useTranslation } from '@/i18n';
import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

// `useLayoutEffect` restores scroll before paint (no visible jump) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Card layout's AC (NA-5529, AC #4): only offer "show remaining" once there
// are more rows than the initial reveal — the table layout is untouched and
// always renders every fetched row, same as before this ticket.
const SHOW_REMAINING_THRESHOLD = 10;

type JournalInfoLayoutSwitchProps = { reportId: string };

const InnerList: FC<JournalInfoLayoutSwitchProps> = memo(({ reportId }) => {
  const { layout, setLayout } = useJournalLayout();
  const { t } = useTranslation();
  const { data: allData, listColumns } = useJournalEntries(reportId);
  const {
    data: cardData,
    isExpanded,
    showRemaining,
    collapse,
  } = useJournalRemaining(allData, reportId);
  const pagination = useDefaultPagination();

  // Card and table layouts have very different heights, so swapping them
  // shifts the drawer's scrollable content and the browser clamps the
  // scroll — the pane appears to jump to the top. Capture the scroll
  // container's position on switch and restore it before paint so toggling
  // layouts stays put. Mirrors `TransactionsDetailLayoutSwitch`.
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const scrollElRef = useRef<Element | null>(null);
  const savedScrollTopRef = useRef<number | null>(null);

  const handleLayoutChange = useCallback(
    (next: JournalLayout) => {
      const scrollEl = getScrollableAncestor(sentinelRef.current);
      scrollElRef.current = scrollEl;
      savedScrollTopRef.current = scrollEl ? scrollEl.scrollTop : null;
      setLayout(next);
    },
    [setLayout]
  );

  useIsomorphicLayoutEffect(() => {
    const scrollEl = scrollElRef.current;
    const saved = savedScrollTopRef.current;
    if (scrollEl && saved !== null) {
      scrollEl.scrollTop = saved;
      savedScrollTopRef.current = null;
    }
  }, [layout]);

  const columns = useColumns();
  const filteredColumns = useMemo(() => {
    if (!listColumns) return columns;
    return filterColumns(columns, listColumns);
  }, [columns, listColumns]);

  const table: InternalTableProps = useMemo(
    () => ({
      data: allData,
      emptyText: <>{t('journal_info_empty_message')}</>,
    }),
    [allData, t]
  );

  const showRemainingButton =
    allData.length > SHOW_REMAINING_THRESHOLD ? (
      <ShowRemainingButton
        isExpanded={isExpanded}
        onShowRemaining={showRemaining}
        onCollapse={collapse}
        collapseLabel={t('journal_entry.collapse')}
        showAllLabel={t('journal_entry.show_all')}
        testIdPrefix='journal-info'
      />
    ) : undefined;

  return (
    <>
      {/* Zero-size anchor used only to locate the scroll container. */}
      <span ref={sentinelRef} aria-hidden='true' style={{ display: 'none' }} />
      <ControlArea
        count={allData.length}
        layout={layout}
        onLayoutChange={handleLayoutChange}
      />
      {layout === JournalLayouts.card ? (
        <JournalInfoCards
          columns={filteredColumns}
          data={cardData}
          totalRecords={allData.length}
          footer={showRemainingButton}
        />
      ) : (
        <JournalInfoTable
          table={table}
          pagination={pagination}
          listColumns={listColumns}
        />
      )}
    </>
  );
});
InnerList.displayName = 'JournalInfoLayoutSwitchInnerList';

export const JournalInfoLayoutSwitch: FC<JournalInfoLayoutSwitchProps> = memo(
  ({ reportId }) => {
    return (
      <Wrapper isProto={false}>
        <ErrorBoundary fallback={ErrorJournalInfoTable}>
          <Suspense fallback={<LoadingJournalInfoTable />}>
            <InnerList reportId={reportId} />
          </Suspense>
        </ErrorBoundary>
      </Wrapper>
    );
  }
);
JournalInfoLayoutSwitch.displayName = 'JournalInfoLayoutSwitch';
